import type { IssueTrackerSettings } from '../data/projectTypes'

// Every project tracks its bugs inside Replay QA; this describes the optional additional destination.
//
//  - 'replay'        — keep bugs in Replay QA only (no external destination).
//  - 'email'         — email a run summary when QA finishes (the per-user completion-email opt-in,
//                      toggled via /api/user-settings notify_on_project_completion — account-wide, so
//                      the host enables it directly rather than persisting anything on the project).
//  - 'github-issues' — file each bug as a GitHub issue on the connected repo, using the GitHub App
//                      installation token (the `github.fileIssues` flag — no PAT needed).
//  - 'gitlab' / 'linear' / 'jira' — file each bug into that tracker (the `issue_tracker` blob + an
//                      API token).

export type BugDestinationKind = 'replay' | 'email' | 'github-issues' | 'gitlab' | 'linear' | 'jira'

// The issue_tracker payload sent to createProject for a linear/jira destination. Mirrors the create
// payload's issue_tracker shape (settings + a one-time apiToken, never apiTokenSet).
export type IssueTrackerCreatePayload = Omit<IssueTrackerSettings, 'apiTokenSet'> & { apiToken?: string }

export interface BugDestinationAnswer {
  kind: BugDestinationKind
  // Present only for 'linear' / 'jira' — the tracker settings + token to persist at create.
  issueTracker?: IssueTrackerCreatePayload
}

// Whether this destination files bugs as GitHub issues on the connected repo (fileIssues flag).
export function destinationUsesGithubIssues(answer: BugDestinationAnswer | null): boolean {
  return answer?.kind === 'github-issues'
}

// The issue_tracker create payload for a destination, or undefined when it doesn't use one.
export function destinationIssueTracker(answer: BugDestinationAnswer | null): IssueTrackerCreatePayload | undefined {
  return (answer?.kind === 'gitlab' || answer?.kind === 'linear' || answer?.kind === 'jira') ? answer.issueTracker : undefined
}
